import * as t from "io-ts";
import { identity, pipe } from "fp-ts/lib/function";
import * as E from "fp-ts/Either";
import { NumberFromString } from "io-ts-types";
import { chainIds, ChainId as ChainIdType, chainKeyMap } from "./chain";

/**
 * @deprecated use `ChainId` instead
 */
export enum ChainIdLegacy {
  MAINNET = 1,
  ETHEREUM = 1,
  ROPSTEN = 3,
  RINKEBY = 4,
  GOERLI = 5,
  OPTIMISM = 10,
  KOVAN = 42,
  MATIC = 137,
  MATIC_TESTNET = 80001,
  ARBITRUM = 42161,
  ARBITRUM_TESTNET = 79377087078960,
  LOCAL = 8545,
  ANVIL = 8118,
  BASE = 8453,
}

export const DEFAULT_CHAIN_ID = ChainIdLegacy.ARBITRUM;
export const DEFAULT_CHAIN_KEY = chainKeyMap[DEFAULT_CHAIN_ID];

export const SUPPORTED_CHAINS = [
  ChainIdLegacy.MAINNET,
  ChainIdLegacy.ARBITRUM,
  ChainIdLegacy.OPTIMISM,
  ChainIdLegacy.BASE,
  ChainIdLegacy.LOCAL,
  ChainIdLegacy.ANVIL,
] as const;

/**
 * @deprecated use `ChainId` instead
 */
export type SupportedChainId = (typeof SUPPORTED_CHAINS)[number];

/**
 * These guards are set up to ensure interop between the legacy
 * `SupportedChainId` and the new `ChainId` type
 */
export const guard1: SupportedChainId[] = [...chainIds];
export const guard2: ChainIdType[] = [...SUPPORTED_CHAINS];

export type SupportedChainValue<T> = Record<SupportedChainId, T>;

export const SupportedChainId = new t.Type<SupportedChainId, number, unknown>(
  "SupportedChainId",
  (value): value is SupportedChainId =>
    isSupportedChainId(value as ChainIdLegacy),
  (u, c) => {
    return pipe(
      t.number.validate(u, c),
      E.chain((number) => {
        try {
          if (isSupportedChainId(number as ChainIdLegacy))
            return t.success(number as SupportedChainId);

          return t.failure(u, c);
        } catch (error) {
          return t.failure(u, c);
        }
      })
    );
  },
  identity
);

export const SupportedChainIdFromString = new t.Type<
  SupportedChainId,
  string,
  unknown
>(
  "SupportedChainIdFromString",
  (value): value is SupportedChainId =>
    isSupportedChainId(value as ChainIdLegacy),
  (u, c) => {
    return pipe(
      NumberFromString.validate(u, c),
      E.chain(SupportedChainId.decode)
    );
  },
  (a) => String(a)
);

export const isSupportedChainId = (
  chainId?: number
): chainId is SupportedChainId =>
  // .includes and .indexOf throw TS errors
  SUPPORTED_CHAINS.some((supportedChain) => supportedChain === chainId);

/**
 * Creates a chain value getter that returns a default value in the case of an
 * undefined or unsupported chain id. Values for all supported chains must be
 * specified.
 */
export const createChainValueGetter =
  <T>(defaultValue: T, chainValues: SupportedChainValue<T>) =>
  (chainId?: ChainIdLegacy): T => {
    if (!chainId || !isSupportedChainId(chainId)) return defaultValue;

    return chainValues[chainId];
  };

export const CHAIN_SLUG: SupportedChainValue<string> = {
  [ChainIdLegacy.ETHEREUM]: "ethereum",
  [ChainIdLegacy.ARBITRUM]: "arbitrum",
  [ChainIdLegacy.OPTIMISM]: "optimism",
  [ChainIdLegacy.BASE]: "base",
  [ChainIdLegacy.LOCAL]: "local",
  [ChainIdLegacy.ANVIL]: "anvil",
};
